import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useExecutionReports } from '@src/providers/ExecutionReportsContext';
import {
  Box,
  bpsToPercent,
  Button,
  ButtonVariants,
  Dropdown,
  FormControlSizes,
  HStack,
  IndicatorBadge,
  IndicatorBadgeVariants,
  LoaderTalos,
  NotificationVariants,
  SideEnum,
  Stack,
  Text,
  ToggleButton,
  Tooltip,
  useCallbackRef,
  useCurrenciesContext,
  useGlobalToasts,
  useSecuritiesContext,
  useSocketClient,
  type DropdownProps,
} from '@talos/kyoko';
import { useEffect, useMemo, useState } from 'react';

import { FillSummary } from '../Components/Quotes/PartialFillButton';
import { RFQSweeper } from '../RFQSweeper';
import { executeRFQSweeper } from '../sweeperExecution';
import { RFQSweepingPanelHeader } from './RFQSweepingPanelHeader';
import { SweeperBlotter } from './SweeperBlotter';
import { SweeperMarketsBlotter } from './SweeperMarketsBlotter';
import type { RFQSweepingSharedProps } from './types';
import { useQuoteSweepingQuoteMarkets } from './useQuoteSweepingQuoteMarkets';

interface RFQSweepingPanelProps extends RFQSweepingSharedProps {
  initialSide: SideEnum;
  dropdownProps: Omit<DropdownProps, 'children'>;
  onClose: () => void;
}

export function RFQSweepingPanel({
  correlationID,
  initialSide,
  buyDisabled = false,
  sellDisabled = false,
  associatedCareOrder,
  dropdownProps,
  onClose,
}: RFQSweepingPanelProps) {
  const quoteSweepingData = useQuoteSweepingQuoteMarkets(correlationID);

  const { currenciesBySymbol } = useCurrenciesContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const webSocketClient = useSocketClient();
  const { add: addToast } = useGlobalToasts();
  const { executionReportSub } = useExecutionReports();
  const { allowedSlippage } = useTradingSettings();
  const { truncateCareOrderID } = useFeatureFlag();

  const canEditSide = !buyDisabled && !sellDisabled;
  const [side, setSide] = useState(initialSide);
  const [sweeper, setSweeper] = useState<RFQSweeper>();

  const representativeQuote = quoteSweepingData?.representativeQuote;
  const currency = representativeQuote?.Currency ? currenciesBySymbol.get(representativeQuote.Currency) : undefined;

  const selectedMarketNames = useMemo(
    () => new Set(sweeper?.fills.map(fill => fill.quote.quoteMarket.Market)),
    [sweeper?.fills]
  );
  const confirmDisabled = sweeper?.sweepDisabled ?? true;
  const createSweeper = useCallbackRef((sweeperSide: SideEnum) => {
    return RFQSweeper.create({
      quotes: quoteSweepingData?.sweeperQuotes ?? [],
      side: sweeperSide,
      currency,
    });
  });

  useEffect(() => {
    if (!representativeQuote) {
      setSweeper(undefined);
      return;
    }

    setSweeper(currentSweeper => {
      // Create the initial sweeper. Do it in a useEffect since we depend on quoteSweepingData which needs to load in.
      if (!currentSweeper) {
        return createSweeper(initialSide);
      }

      return currentSweeper.updateQuotes(quoteSweepingData.sweeperQuotes);
    });
  }, [createSweeper, initialSide, representativeQuote, quoteSweepingData?.sweeperQuotes]);

  const handleToggleMarket = useCallbackRef((marketName: string) => {
    setSweeper(currentSweeper =>
      currentSweeper?.fills.some(fill => fill.quote.quoteMarket.Market === marketName)
        ? currentSweeper.removeMarket(marketName)
        : currentSweeper?.addMarket(marketName)
    );
  });

  const handleRemoveMarket = useCallbackRef((marketName: string) => {
    setSweeper(currentSweeper => currentSweeper?.removeMarket(marketName));
  });

  const handleSideChange = useCallbackRef((nextSide: SideEnum) => {
    if (!canEditSide || nextSide === side) {
      return;
    }

    setSide(nextSide);
    setSweeper(currentSweeper => currentSweeper?.updateSide(nextSide));
  });

  const handleDesiredQtyChange = useCallbackRef((marketName: string, value: string) => {
    setSweeper(currentSweeper => currentSweeper?.setDesiredQty(marketName, value));
  });

  const handleCancel = useCallbackRef(() => {
    onClose();
  });

  const handleConfirm = useCallbackRef(async () => {
    if (!representativeQuote || !sweeper || confirmDisabled) {
      return;
    }

    const security = securitiesBySymbol.get(representativeQuote.Symbol);

    if (!security) {
      addToast({
        text: `Cannot sweep RFQ: missing security details for ${representativeQuote.Symbol}.`,
        variant: NotificationVariants.Negative,
        dismissable: true,
        timeout: 5000,
      });
      return;
    }

    await executeRFQSweeper({
      sweeper,
      security,
      webSocketClient,
      executionReportsSub: executionReportSub,
      addToast,
      allowedSlippage: allowedSlippage?.value ? bpsToPercent(allowedSlippage.value) : undefined,
      truncateCareOrderID,
      associatedCareOrder,
      onUpdate: setSweeper,
    });
  });

  return (
    <Dropdown portalize isParentDropdown {...dropdownProps}>
      {quoteSweepingData == null || sweeper == null ? (
        <LoaderTalos />
      ) : (
        <Stack h="100%" gap="1px" background="backgroundBody">
          <RFQSweepingPanelHeader quote={quoteSweepingData.representativeQuote} />

          <Box minHeight="164px" w="100%">
            <SweeperMarketsBlotter
              quoteSweepingData={quoteSweepingData}
              side={side}
              selectedMarketNames={selectedMarketNames}
              onToggleMarket={handleToggleMarket}
            />
          </Box>

          <HStack
            gap="spacing4"
            w="100%"
            px="spacing8"
            py="spacing6"
            justifyContent="flex-start"
            background="colors.gray.030"
          >
            <Text color="colorTextImportant" fontSize="fontSizeSm">
              Sweeping Basket
            </Text>
            {canEditSide ? <SideToggle side={side} onChange={handleSideChange} /> : <SideBadge side={side} />}
          </HStack>

          <Box minHeight="164px" w="100%">
            <SweeperBlotter
              sweeper={sweeper}
              symbol={quoteSweepingData.representativeQuote.Symbol}
              pricingMode={quoteSweepingData.representativeQuote.PricingMode}
              onDesiredQtyChange={handleDesiredQtyChange}
              onRemoveMarket={handleRemoveMarket}
            />
          </Box>

          <Box px="spacing8" py="spacing12" background="colors.gray.030" fontSize="fontSizeSm">
            <FillSummary
              desiredQty={sweeper.totalQty ?? '0'}
              quote={quoteSweepingData.representativeQuote}
              layout="horizontal"
            />
          </Box>

          <HStack
            flex="1"
            px="spacing12"
            background="colors.gray.020"
            alignItems="center"
            justifyContent="space-between"
          >
            <Button onClick={handleCancel}>Cancel</Button>
            <Tooltip tooltip={sweeper.error} disabled={!sweeper.error}>
              <Button
                variant={sweeper.side === SideEnum.Buy ? ButtonVariants.Positive : ButtonVariants.Negative}
                onClick={handleConfirm}
                disabled={confirmDisabled}
                loading={sweeper.loading}
                data-testid="rfq-sweeping-confirm-button"
              >
                Confirm {sweeper.side}
              </Button>
            </Tooltip>
          </HStack>
        </Stack>
      )}
    </Dropdown>
  );
}

function SideToggle({ side, onChange }: { side: SideEnum; onChange: (side: SideEnum) => void }) {
  return (
    <HStack gap="spacing2">
      <ToggleButton
        selected={side === SideEnum.Sell}
        selectedVariant={ButtonVariants.Negative}
        size={FormControlSizes.Xxs}
        onClick={() => onChange(SideEnum.Sell)}
        data-testid="rfq-sweeping-sell-toggle"
      >
        Sell
      </ToggleButton>
      <ToggleButton
        selected={side === SideEnum.Buy}
        selectedVariant={ButtonVariants.Positive}
        size={FormControlSizes.Xxs}
        onClick={() => onChange(SideEnum.Buy)}
        data-testid="rfq-sweeping-buy-toggle"
      >
        Buy
      </ToggleButton>
    </HStack>
  );
}

function SideBadge({ side }: { side: SideEnum }) {
  return (
    <IndicatorBadge variant={side === SideEnum.Buy ? IndicatorBadgeVariants.Positive : IndicatorBadgeVariants.Negative}>
      {side}
    </IndicatorBadge>
  );
}
